import React, { useState, useEffect, memo, useRef } from "react";
import { useTranslation } from 'react-i18next';
import apiRequest from "../../services/apiClient";
import { cardTypeDisplayText, copyToClipboard, currencyDisplayText, labelTextSecretKey, labelTextSecretValue, openNewWindows, secretTypeDisplayTextTranslated } from "../../utils/utils";
import { secretTypesConstants } from "../../utils/enums";
import { useSnackbar } from 'notistack';

// Manager para el singleton del detalle de secret
class SecretDetailManager {
  constructor() {
    this.item = null;
    this.loading = false;
    this.showSecretKey = {
      showSecret: false,
      secretDecodedValue: "",
      loadingDecodedValue: false
    };
    this.showSecretValue = {
      showSecret: false,
      secretDecodedValue: "",
      loadingDecodedValue: false
    };
    this.listeners = new Set();
    this.currentRequest = null;
  }

  subscribe(listener) {
    this.listeners.add(listener);
    listener(this.getState());
  }

  unsubscribe(listener) {
    this.listeners.delete(listener);
  }

  getState() {
    return {
      item: this.item,
      loading: this.loading,
      showSecretKey: this.showSecretKey,
      showSecretValue: this.showSecretValue,
    };
  }

  notifyListeners() {
    this.listeners.forEach(listener => listener(this.getState()));
  }

  async loadSecret(secretItemId) {
    if (this.loading) {
      console.log('Already loading secret details, ignoring request for:', secretItemId);
      return;
    }

    this.loading = true;
    this.showSecretKey = {
      showSecret: false,
      secretDecodedValue: "",
      loadingDecodedValue: false
    };
    this.showSecretValue = {
      showSecret: false,
      secretDecodedValue: "",
      loadingDecodedValue: false
    };
    this.notifyListeners();

    // Cancelar petición anterior si existe
    if (this.currentRequest) {
      this.currentRequest.cancel?.();
    }

    const request = apiRequest(`secretItem/${secretItemId}?$expand=userCreator($select=fullName),userModifier($select=fullName),section($select=id,name),client($select=id,name)`);
    this.currentRequest = request;

    try {
      const response = await request;
      if (this.currentRequest === request) {
        this.item = response.data;
      }
    } catch (error) {
      if (this.currentRequest === request) {
        console.error('Error loading secret details:', error);
      }
    } finally {
      if (this.currentRequest === request) {
        this.loading = false;
        this.currentRequest = null;
        this.notifyListeners();
      }
    }
  }

  async handleShowSecretKey(secretItemId) {
    if (this.showSecretKey.showSecret || this.showSecretKey.secretDecodedValue) {
      this.showSecretKey = {
        ...this.showSecretKey,
        showSecret: !this.showSecretKey.showSecret,
        loadingDecodedValue: false
      };
    } else {
      this.showSecretKey = {
        ...this.showSecretKey,
        loadingDecodedValue: true
      };
      this.notifyListeners();

      try {
        const response = await apiRequest(`secretItem/showSecretKey?secretId=${secretItemId}`);
        this.showSecretKey = {
          ...this.showSecretKey,
          showSecret: !this.showSecretKey.showSecret,
          secretDecodedValue: response.data.secretKey,
          loadingDecodedValue: false
        };
      } catch (error) {
        this.showSecretKey = {
          ...this.showSecretKey,
          loadingDecodedValue: false
        };
      }
    }
    this.notifyListeners();
  }

  async handleShowSecretValue(secretItemId) {
    if (this.showSecretValue.showSecret || this.showSecretValue.secretDecodedValue) {
      this.showSecretValue = {
        ...this.showSecretValue,
        showSecret: !this.showSecretValue.showSecret,
        loadingDecodedValue: false
      };
    } else {
      this.showSecretValue = {
        ...this.showSecretValue,
        loadingDecodedValue: true
      };
      this.notifyListeners();

      try {
        const response = await apiRequest(`secretItem/showSecretValue?secretId=${secretItemId}`);
        this.showSecretValue = {
          ...this.showSecretValue,
          showSecret: !this.showSecretValue.showSecret,
          secretDecodedValue: response.data.secretValue,
          loadingDecodedValue: false
        };
      } catch (error) {
        this.showSecretValue = {
          ...this.showSecretValue,
          loadingDecodedValue: false
        };
      }
    }
    this.notifyListeners();
  }

  async handleCopySecretKey(secretItemId, enqueueSnackbar) {
    if (this.showSecretKey.secretDecodedValue) {
      copyToClipboard(this.showSecretKey.secretDecodedValue);
      enqueueSnackbar('Text successfully copied!', { variant: 'info', autoHideDuration: 1000 });
    } else {
      this.showSecretKey = {
        ...this.showSecretKey,
        loadingDecodedValue: true
      };
      this.notifyListeners();

      try {
        const response = await apiRequest(`secretItem/showSecretKey?secretId=${secretItemId}`);
        this.showSecretKey = {
          ...this.showSecretKey,
          secretDecodedValue: response.data.secretKey,
          loadingDecodedValue: false
        };
        copyToClipboard(response.data.secretKey);
        enqueueSnackbar('Text successfully copied!', { variant: 'info', autoHideDuration: 1000 });
      } catch (error) {
        this.showSecretKey = {
          ...this.showSecretKey,
          loadingDecodedValue: false
        };
      }
      this.notifyListeners();
    }
  }

  async handleCopySecretValue(secretItemId, enqueueSnackbar) {
    if (this.showSecretValue.secretDecodedValue) {
      copyToClipboard(this.showSecretValue.secretDecodedValue);
      enqueueSnackbar('Text successfully copied!', { variant: 'info', autoHideDuration: 1000 });
    } else {
      this.showSecretValue = {
        ...this.showSecretValue,
        loadingDecodedValue: true
      };
      this.notifyListeners();

      try {
        const response = await apiRequest(`secretItem/showSecretValue?secretId=${secretItemId}`);
        this.showSecretValue = {
          ...this.showSecretValue,
          secretDecodedValue: response.data.secretValue,
          loadingDecodedValue: false
        };
        copyToClipboard(response.data.secretValue);
        enqueueSnackbar('Text successfully copied!', { variant: 'info', autoHideDuration: 1000 });
      } catch (error) {
        this.showSecretValue = {
          ...this.showSecretValue,
          loadingDecodedValue: false
        };
      }
      this.notifyListeners();
    }
  }

  resetState() {
    this.item = null;
    this.loading = false;
    this.showSecretKey = {
      showSecret: false,
      secretDecodedValue: "",
      loadingDecodedValue: false
    };
    this.showSecretValue = {
      showSecret: false,
      secretDecodedValue: "",
      loadingDecodedValue: false
    };
    this.currentRequest = null;
    this.notifyListeners();
  }
}

const secretDetailManager = new SecretDetailManager();

const SecretDetailSingleton = memo(({ selectedItem, handleUnselectItem, handleEditItem, handleDeleteItem }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const [detailState, setDetailState] = useState(secretDetailManager.getState());

  const updateDetailState = (newState) => {
    setDetailState(newState);
  };

  useEffect(() => {
    secretDetailManager.subscribe(updateDetailState);
    return () => secretDetailManager.unsubscribe(updateDetailState);
  }, []);

  useEffect(() => {
    if (selectedItem) {
      secretDetailManager.loadSecret(selectedItem);
    } else {
      secretDetailManager.resetState();
    }
  }, [selectedItem]);

  const handleCopyText = (text) => {
    copyToClipboard(text);
    enqueueSnackbar('Text successfully copied!', { variant: 'info', autoHideDuration: 1000 });
  };

  const { item, loading, showSecretKey, showSecretValue } = detailState;

  if (!item || loading) {
    return (
      <div className="p-6 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
        <div className="space-y-3">
          <div className="h-4 bg-gray-200 rounded"></div>
          <div className="h-4 bg-gray-200 rounded"></div>
          <div className="h-4 bg-gray-200 rounded"></div>
          <div className="h-4 bg-gray-200 rounded"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-6">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <input
              type="text"
              value={secretTypeDisplayTextTranslated(item.type, t)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
              readOnly
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Section</label>
            <input
              type="text"
              value={item.section.name}
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
              readOnly
            />
          </div>

          {item.client && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
              <input
                type="text"
                value={item.client?.name}
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                readOnly
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <div className="relative">
              <input
                type="text"
                value={item.name}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md bg-gray-50"
                readOnly
              />
              <button
                type="button"
                onClick={() => handleCopyText(item.name)}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700"
                title="Copy"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </button>
            </div>
          </div>

          {item.host && item.type !== secretTypesConstants.CreditCards && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Host</label>
              <div className="relative">
                <input
                  type="text"
                  value={item.host}
                  className="w-full px-3 py-2 pr-20 border border-gray-300 rounded-md bg-gray-50"
                  readOnly
                />
                <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex space-x-1">
                  {item.host.startsWith("http") && (
                    <button
                      type="button"
                      onClick={() => openNewWindows(item.host)}
                      className="p-1 text-gray-500 hover:text-gray-700"
                      title="Open Link"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => handleCopyText(item.host)}
                    className="p-1 text-gray-500 hover:text-gray-700"
                    title="Copy"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}

          {(item?.type === secretTypesConstants.CreditCards ||
            item?.type === secretTypesConstants.BankAccounts ||
            item?.type === secretTypesConstants.BankDetractionAccounts) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
              <input
                type="text"
                value={currencyDisplayText(item.currency)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                readOnly
              />
            </div>
          )}

          {(item?.type === secretTypesConstants.BankAccounts ||
            item?.type === secretTypesConstants.BankDetractionAccounts) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Account Number</label>
              <div className="relative">
                <input
                  type="text"
                  value={item.accountNumber}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md bg-gray-50"
                  readOnly
                />
                <button
                  type="button"
                  onClick={() => handleCopyText(item.accountNumber)}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700"
                  title="Copy"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {item?.type === secretTypesConstants.BankAccounts && item.accountNumberCCI && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Account Number CCI</label>
              <div className="relative">
                <input
                  type="text"
                  value={item.accountNumberCCI}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md bg-gray-50"
                  readOnly
                />
                <button
                  type="button"
                  onClick={() => handleCopyText(item.accountNumberCCI)}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700"
                  title="Copy"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {item?.type === secretTypesConstants.BankAccounts && item.internationalInformationBank && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">International Information Bank</label>
              <div className="relative">
                <input
                  type="text"
                  value={item.internationalInformationBank}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md bg-gray-50"
                  readOnly
                />
                <button
                  type="button"
                  onClick={() => handleCopyText(item.internationalInformationBank)}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700"
                  title="Copy"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {item?.type === secretTypesConstants.BankAccounts && item.internationalInformationAddress && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">International Information Address</label>
              <div className="relative">
                <input
                  type="text"
                  value={item.internationalInformationAddress}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md bg-gray-50"
                  readOnly
                />
                <button
                  type="button"
                  onClick={() => handleCopyText(item.internationalInformationAddress)}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700"
                  title="Copy"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {item?.type === secretTypesConstants.BankAccounts && item.internationalInformationSwift && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">International Information Swift</label>
              <div className="relative">
                <input
                  type="text"
                  value={item.internationalInformationSwift}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md bg-gray-50"
                  readOnly
                />
                <button
                  type="button"
                  onClick={() => handleCopyText(item.internationalInformationSwift)}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700"
                  title="Copy"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {item?.type === secretTypesConstants.SunatCredentials && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sunat RUC</label>
              <div className="relative">
                <input
                  type="text"
                  value={item.sunatInformationRUC}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md bg-gray-50"
                  readOnly
                />
                <button
                  type="button"
                  onClick={() => handleCopyText(item.sunatInformationRUC)}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700"
                  title="Copy"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {item?.type === secretTypesConstants.CreditCards && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Card Type</label>
              <input
                type="text"
                value={cardTypeDisplayText(item.cardType)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                readOnly
              />
            </div>
          )}

          {item.secretKey && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{labelTextSecretKey(item.type)}</label>
              <div className="relative">
                <input
                  type={!showSecretKey.showSecret ? 'password' : 'text'}
                  value={!showSecretKey.showSecret ? item.secretKey : showSecretKey.secretDecodedValue}
                  className="w-full px-3 py-2 pr-20 border border-gray-300 rounded-md bg-gray-50"
                  readOnly
                />
                <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex space-x-1">
                  {showSecretKey.loadingDecodedValue && (
                    <svg className="animate-spin h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  <button
                    type="button"
                    onClick={() => secretDetailManager.handleShowSecretKey(selectedItem)}
                    className="p-1 text-gray-500 hover:text-gray-700"
                    title={!showSecretKey.showSecret ? "Show" : "Hide"}
                  >
                    {!showSecretKey.showSecret ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                      </svg>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => secretDetailManager.handleCopySecretKey(selectedItem, enqueueSnackbar)}
                    className="p-1 text-gray-500 hover:text-gray-700"
                    title="Copy"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}

          {item.secretValue && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{labelTextSecretValue(item.type)}</label>
              <div className="relative">
                <input
                  type={!showSecretValue.showSecret ? 'password' : 'text'}
                  value={!showSecretValue.showSecret ? item.secretValue : showSecretValue.secretDecodedValue}
                  className="w-full px-3 py-2 pr-20 border border-gray-300 rounded-md bg-gray-50"
                  readOnly
                />
                <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex space-x-1">
                  {showSecretValue.loadingDecodedValue && (
                    <svg className="animate-spin h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  <button
                    type="button"
                    onClick={() => secretDetailManager.handleShowSecretValue(selectedItem)}
                    className="p-1 text-gray-500 hover:text-gray-700"
                    title={!showSecretValue.showSecret ? "Show" : "Hide"}
                  >
                    {!showSecretValue.showSecret ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                      </svg>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => secretDetailManager.handleCopySecretValue(selectedItem, enqueueSnackbar)}
                    className="p-1 text-gray-500 hover:text-gray-700"
                    title="Copy"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}

          {item?.type === secretTypesConstants.CreditCards && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Card Expiration</label>
              <div className="relative">
                <input
                  type="text"
                  value={item.cardExpiration}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md bg-gray-50"
                  readOnly
                />
                <button
                  type="button"
                  onClick={() => handleCopyText(item.cardExpiration)}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700"
                  title="Copy"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {item?.type === secretTypesConstants.CreditCards && item.userName && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">User Name</label>
              <div className="relative">
                <input
                  type="text"
                  value={item.userName}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md bg-gray-50"
                  readOnly
                />
                <button
                  type="button"
                  onClick={() => handleCopyText(item.userName)}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700"
                  title="Copy"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {item?.type === secretTypesConstants.CreditCards && item.userLastName && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">User Last Name</label>
              <div className="relative">
                <input
                  type="text"
                  value={item.userLastName}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md bg-gray-50"
                  readOnly
                />
                <button
                  type="button"
                  onClick={() => handleCopyText(item.userLastName)}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700"
                  title="Copy"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {item?.type === secretTypesConstants.CreditCards && item.userEmail && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">User Email</label>
              <div className="relative">
                <input
                  type="text"
                  value={item.userEmail}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md bg-gray-50"
                  readOnly
                />
                <button
                  type="button"
                  onClick={() => handleCopyText(item.userEmail)}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700"
                  title="Copy"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {item.notes && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <div className="relative">
                <textarea
                  value={item.notes}
                  rows={3}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md bg-gray-50 resize-none"
                  readOnly
                />
                <button
                  type="button"
                  onClick={() => handleCopyText(item.notes)}
                  className="absolute right-2 top-2 p-1 text-gray-500 hover:text-gray-700"
                  title="Copy"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Created</label>
            <input
              type="text"
              value={new Date(item.created).toLocaleString()}
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
              readOnly
            />
          </div>

          {item.updated && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Updated</label>
              <input
                type="text"
                value={new Date(item.updated).toLocaleString()}
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                readOnly
              />
            </div>
          )}

          {item.userCreator && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">User Creator</label>
              <input
                type="text"
                value={item.userCreator?.fullName}
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                readOnly
              />
            </div>
          )}

          {item.userModifier && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">User Modifier</label>
              <input
                type="text"
                value={item.userModifier?.fullName}
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                readOnly
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Identifier</label>
            <input
              type="text"
              value={item.id}
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
              readOnly
            />
          </div>
        </div>
      </div>

      {/* Fixed Actions */}
      <div className="p-6 pt-4 border-t border-gray-200 bg-white">
        <div className="flex space-x-3">
          <button
            onClick={handleUnselectItem}
            className="flex-1 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md transition-colors"
          >
            Close
          </button>
          <button
            onClick={handleEditItem}
            className="flex-1 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md transition-colors"
          >
            Edit
          </button>
          <button
            onClick={handleDeleteItem}
            className="flex-1 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-md transition-colors"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
});

SecretDetailSingleton.displayName = 'SecretDetailSingleton';

export default SecretDetailSingleton;
